/**
 * D-018 — THE TABLE. Howard's own dice, one throw at a time.
 *
 * "Manual dice entry (D-018) — do it properly or not at all; it means `Dice`
 *  becomes a source you can feed, every roll gets marked as hand-entered in the
 *  ledger, and the turn loop has to pause mid-turn. Not a text box."
 *
 * This is the screen half of that, and the sentence it is built against is the
 * last one. It is NOT a text box: it does not take a race's worth of numbers up
 * front, it does not take a turn's worth, and it does not take a throw's worth.
 * It asks for ONE DIE — the one the engine is actually blocked on, eight frames
 * down inside a half-resolved turn — names which physical die that is, shows
 * what is already on the felt beside it, and waits.
 *
 * WHAT IT KNOWS AND WHAT IT DOES NOT SAY. The prompt names the notation, the
 * coloured die, the faces already down and the phase of the turn that asked.
 * It does NOT name a driver, because the dice source genuinely cannot know one
 * (see `DieDraw.phase`), and a driver's name printed here on a guess would be
 * the invented result this project bans. The driver IS in the ledger the moment
 * the roll resolves, which is where the feed and the boards already show it.
 *
 * NOTHING IN HERE IS A RULE. It holds a session — `ManualRace` for an
 * exhibition, `ManualSeasonRace` for race N of a career — and renders what that
 * session reports. Every face it collects goes into the engine and comes back
 * out in `RaceResult.diceStream`, marked `hand`, forever.
 *
 * TWO KINDS OF RACE, ONE TABLE. `ManualRace` and `ManualSeasonRace` have the
 * same shape by design (`start` / `supply` / `undo` / `finishWithEngine`) and
 * their `need` steps are the same object; only what comes back at the flag
 * differs — a `RaceResult` from one, a whole `SeasonRaceStep` from the other.
 * So this screen takes either and the caller says which, rather than a second
 * copy of the dice table existing for season races. A second copy is how the
 * two would start disagreeing about what a hand-rolled race looks like.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ManualRace, type ManualDiceStep } from '../domain/manualRace.ts';
import {
  ManualSeasonRace,
  type ManualSeasonStep,
  type SeasonRaceStep,
  type SeasonState,
} from '../domain/seasonPlay.ts';
import type { RaceOptions, RaceResult, RaceSetup } from '../domain/race.ts';
import type { DiceRequest } from '../domain/dice.ts';
import WoodenDice from './WoodenDice.tsx';
import { enteredTableDice } from '../presentation/jointDice.ts';

/** D-018 for a season race: race N of the live season, rolled by hand. */
export interface DiceTableSeason {
  /** The season as it stands BEFORE this race. Never mutated. */
  state: SeasonState;
  /** The race, the week and the season one race on. */
  onFinished: (step: SeasonRaceStep) => void;
}

export interface DiceTableProps {
  /** Exhibition: the race to run, identical to what `runRace` would be handed. */
  setup?: RaceSetup;
  options?: RaceOptions;
  /** Season: race N of a career. Give this or `setup`, never both. */
  season?: DiceTableSeason;
  /** The finished exhibition race — what `runRace` returns, plus a stream. */
  onFinished?: (result: RaceResult) => void;
  /** Backing out before the green. Nothing has been run, so nothing is lost. */
  onCancel: () => void;
  /**
   * D-EZ-103 — false while the player is on another tab. The table stays
   * mounted (its faces exist nowhere else), but the number keys stop entering
   * faces, so typing elsewhere can never roll a die by accident.
   */
  active?: boolean;
  /** D-EZ-103 — where the hand-rolled race has got to, for the "Your dice" tab. */
  onProgress?: (progress: { turn: number; faces: number }) => void;
}

/**
 * The CSS class for the die the engine is asking for, so the button on screen
 * is the colour of the thing on the table. Falls back to metallic, which is
 * what an unnamed die looks like in the rest of the interface.
 */
function dieClass(role: string | undefined): string {
  if (role === 'red' || role === 'white' || role === 'blue' || role === 'black') return `die--${role}`;
  return 'die--metallic';
}

/** "the RED die" / "a d10" — the noun the prompt and the buttons agree on. */
function dieNoun(request: DiceRequest): string {
  if (request.role && ['red', 'white', 'blue', 'black', 'metallic'].includes(request.role)) {
    return `${request.role} die`;
  }
  return `d${request.sides}`;
}

export default function DiceTable({
  setup,
  options,
  season,
  onFinished,
  onCancel,
  active = true,
  onProgress,
}: DiceTableProps) {
  /**
   * ONE SESSION FOR THE WHOLE RACE, held in a ref.
   *
   * It is not state because it is not rendered — `step` is. Rebuilding it on a
   * re-render would throw away the face list, which is the only thing in this
   * screen that cannot be recomputed.
   */
  const session = useRef<ManualRace | ManualSeasonRace | null>(null);
  if (session.current === null) {
    if (season) session.current = new ManualSeasonRace(season.state, options ? { race: options } : {});
    else if (setup) session.current = new ManualRace(setup, options);
    // Neither is a caller bug, and it throws rather than rendering an empty
    // table that looks like a race waiting on a die it will never ask for.
    else throw new Error('DiceTable needs either an exhibition `setup` or a `season` to roll');
  }

  const [step, setStep] = useState<ManualDiceStep | ManualSeasonStep>(() =>
    (session.current as ManualRace | ManualSeasonRace).start(),
  );
  const [refused, setRefused] = useState<string | null>(null);

  /**
   * Hand the engine a face.
   *
   * `supply` refuses an impossible face by throwing, and the refusal is shown
   * rather than swallowed: a 7 typed for a d6 means the player and the engine
   * disagree about which die is in their hand, and that is worth interrupting
   * for.
   */
  const give = useCallback((face: number) => {
    const s = session.current;
    if (!s) return;
    try {
      setRefused(null);
      setStep(s.supply(face));
    } catch (e) {
      setRefused(String((e as Error)?.message ?? e));
    }
  }, []);

  const takeBack = useCallback(() => {
    const s = session.current;
    if (!s) return;
    setRefused(null);
    setStep(s.undo(1));
  }, []);

  /**
   * The race is over the moment the engine stops asking. Reported out on an
   * effect rather than inside `give`, so the parent is never called during this
   * component's own render.
   *
   * The two `done` shapes are told apart by their own fields — a season step
   * carries the whole `SeasonRaceStep`, an exhibition carries the `RaceResult`
   * — so neither caller can be handed the other's object.
   */
  useEffect(() => {
    if (step.status !== 'done') return;
    if ('step' in step) season?.onFinished(step.step);
    else onFinished?.(step.result);
  }, [step, onFinished, season]);

  /**
   * Number keys enter a face, backspace takes one back.
   *
   * A player with dice in one hand should not have to find a button with the
   * other. Bound only while a die is actually wanted, and only for faces the
   * pending die can show — pressing 7 for a d6 does nothing at all, rather than
   * being refused with a message the player did not ask for.
   */
  useEffect(() => {
    if (step.status !== 'need' || !onProgress) return;
    onProgress({ turn: step.turns.length + 1, faces: step.stream.length });
  }, [step, onProgress]);

  useEffect(() => {
    if (step.status !== 'need' || !active) return;
    const sides = step.request.sides;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Backspace') {
        e.preventDefault();
        takeBack();
        return;
      }
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= Math.min(sides, 9)) {
        e.preventDefault();
        give(n);
      }
      // '0' reads as 10 on a d10, which is how a ten-sided die is printed.
      if (e.key === '0' && sides === 10) {
        e.preventDefault();
        give(10);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step, give, takeBack, active]);

  const recent = useMemo(
    () => (step.status === 'need' ? step.stream.slice(-14) : []),
    [step],
  );

  if (step.status === 'done') {
    return (
      <section className="dicetable" aria-label="Hand-rolled race">
        <p className="dicetable__prompt">Checkered flag. Taking you to the race.</p>
      </section>
    );
  }

  const { request, turns, stream, prompt } = step;
  const faceButtons = Array.from({ length: request.sides }, (_, i) => i + 1);

  return (
    <section className="dicetable" aria-label="Roll your own dice">
      <header className="dicetable__head">
        <span className="dicetable__badge">Your dice</span>
        <span className="dim">
          {turns.length} turn{turns.length === 1 ? '' : 's'} complete · {stream.length} face
          {stream.length === 1 ? '' : 's'} entered
        </span>
      </header>

      <WoodenDice dice={enteredTableDice(request)} />

      {/* The ask. One die, named, with what is already down beside it. */}
      <p className="dicetable__prompt">{prompt}</p>
      {request.phase && <p className="dicetable__phase">{request.phase}</p>}

      {request.given.length > 0 && (
        <div className="dicetable__given" aria-label="Already on the table">
          {request.given.map((face, i) => (
            <span key={i} className={`die ${dieClass(request.roles[i])}`}>
              {face}
            </span>
          ))}
          <span className={`die ${dieClass(request.role)} dicetable__waiting`} aria-hidden>
            ?
          </span>
        </div>
      )}

      <div className="dicetable__faces" role="group" aria-label={`What did the ${dieNoun(request)} come up?`}>
        {faceButtons.map((face) => (
          <button
            key={face}
            type="button"
            className={`dicetable__face die ${dieClass(request.role)}`}
            onClick={() => give(face)}
            title={`${dieNoun(request)} — ${face}`}
          >
            {face}
          </button>
        ))}
      </div>

      {refused && <p className="dicetable__refused">{refused}</p>}

      <div className="dicetable__row">
        <button type="button" className="btn btn--quiet" onClick={takeBack} disabled={stream.length === 0}>
          Take that one back
        </button>
        {/*
          The ONE place the engine is allowed to roll for you once your dice are
          on the table, and it is a button you have to press. The turn that
          straddles the crossover is marked `mixed` in the ledger rather than
          claimed as hand-rolled — see `DiceRecord.entry`.
        */}
        <button
          type="button"
          className="btn btn--quiet"
          onClick={() => {
            const s = session.current;
            if (!s) return;
            if (s instanceof ManualSeasonRace) season?.onFinished(s.finishWithEngine());
            else onFinished?.(s.finishWithEngine());
          }}
        >
          Let the game roll the rest
        </button>
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Back to setup
        </button>
      </div>

      {/* The last few faces, in the physical colour of the die that made them.
          A window on what has been entered, so a mis-key is visible before it
          is fourteen throws old. */}
      <ol className="dicetable__recent" aria-label="Recently entered">
        {recent.map((draw) => (
          <li key={draw.index} className="dicetable__recentitem">
            <span className={`die ${dieClass(draw.role)}`}>{draw.face}</span>
            <span className="dim">{draw.notation}</span>
          </li>
        ))}
        {recent.length === 0 && <li className="dicetable__recentitem dim">Nothing thrown yet.</li>}
      </ol>
    </section>
  );
}
